import { ChertoExperiments } from 'constants/experiments'
import { getChertoExperimentVariation } from 'selectors/experimentSelectors'

type InsurancePriceVariation = (typeof ChertoExperiments.croInsurancePriceIncreaseV2.variations)[number]

/**
 * Signed percentage each arm of the insurance price experiment applies to the customer-facing
 * CoverGenius premium. Control, and any unknown or off state, leaves the price untouched.
 */
export const INSURANCE_PRICE_ADJUSTMENT_BY_VARIATION: Partial<Record<InsurancePriceVariation, number>> = {
  'variant-plus-5': 5,
  'variant-minus-5': -5,
}

/**
 * State-based read of the signed percentage the current user's arm of the insurance price
 * experiment (a Cherto experiment) applies to the insurance price, or `undefined` when the price
 * stays untouched.
 *
 * Reselect selectors (and the checkout quote read-path) cannot call the `useExperiment` hook, so
 * the decision is derived directly from `state.cherto`. This mirrors useExperiment's cherto branch
 * — the UTM/GHA exclusion, the `CHERTO_<key>` local override, and case-insensitive variation keys —
 * so the value matches the hook's `variation`, keeping the write (fetch hook) and read (checkout
 * selectors) quote keys identical for every arm. Activation is fired separately by
 * `useInsurancePriceExperiment` from the surfaces that show insurance prices.
 */
export function selectInsurancePriceAdjustmentPercentage(state: App.State): number | undefined {
  const variationKey = getChertoExperimentVariation(state, ChertoExperiments.croInsurancePriceIncreaseV2)
  if (!variationKey) {
    return undefined
  }

  return INSURANCE_PRICE_ADJUSTMENT_BY_VARIATION[String(variationKey).toLowerCase() as InsurancePriceVariation]
}
